import {
  Message01Icon,
  RefreshIcon,
  SentIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Markdown } from "@/components/markdown";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Bubble, BubbleContent } from "@/components/ui/bubble";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Field, FieldGroup } from "@/components/ui/field";
import { Marker, MarkerContent } from "@/components/ui/marker";
import {
  Message,
  MessageAvatar,
  MessageContent,
  MessageHeader,
} from "@/components/ui/message";
import {
  MessageScroller,
  MessageScrollerButton,
  MessageScrollerContent,
  MessageScrollerItem,
  MessageScrollerProvider,
  MessageScrollerViewport,
} from "@/components/ui/message-scroller";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import {
  hasPlayableDialogue,
  judgePuzzle,
  parseSystemDialogue,
  pickChoice,
  revealHint,
  skipToPuzzles,
  startDialogue,
  submitPuzzleAnswer,
  type DialoguePerson,
  type DialogueState,
  type SystemDialogue,
} from "@/lib/system-dialogue";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  versionId: string | null;
};

function initials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  return trimmed.slice(0, 1);
}

function PersonAvatar({ person }: { person: DialoguePerson }) {
  if (person.avatarUrl) {
    return (
      <img
        src={person.avatarUrl}
        alt=""
        className="size-8 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex size-8 items-center justify-center rounded-full bg-muted text-[0.7rem] font-medium">
      {initials(person.name)}
    </span>
  );
}

function phaseLabel(state: DialogueState): string {
  if (state.phase === "guide") return "引导页";
  if (state.phase === "puzzle" && state.input.type === "puzzle") {
    return `任务 ${state.input.index + 1}/${state.input.total}`;
  }
  if (state.phase === "done") return "结案";
  return "任务";
}

export function SystemDialogueSheet({ open, onOpenChange, versionId }: Props) {
  const query = useQuery({
    queryKey: ["phone-json", versionId],
    queryFn: () => api.getPhoneJson(versionId!),
    enabled: open && Boolean(versionId),
  });

  const dialogue = useMemo(
    () => parseSystemDialogue(query.data?.phoneJson ?? null),
    [query.data?.phoneJson],
  );

  const [state, setState] = useState<DialogueState | null>(null);
  const [startedKey, setStartedKey] = useState("");
  const [draft, setDraft] = useState("");

  const playKey = `${versionId ?? ""}:${open ? "open" : "closed"}`;

  useEffect(() => {
    if (!open) {
      setStartedKey("");
      setDraft("");
      return;
    }
    if (query.isLoading) return;
    if (startedKey === playKey) return;
    setStartedKey(playKey);
    setDraft("");
    setState(hasPlayableDialogue(dialogue) ? startDialogue(dialogue) : null);
  }, [open, playKey, dialogue, query.isLoading, startedKey]);

  const restart = (next: SystemDialogue, from: "guide" | "puzzles") => {
    setDraft("");
    setState(from === "puzzles" ? skipToPuzzles(next) : startDialogue(next));
  };

  const sendAnswer = () => {
    if (!dialogue || !state) return;
    setState(submitPuzzleAnswer(dialogue, state, draft));
    setDraft("");
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full max-w-md flex-col overflow-hidden sm:max-w-md data-[side=right]:w-full data-[side=right]:max-w-md data-[side=right]:sm:max-w-md"
      >
        <SheetHeader className="gap-2">
          <SheetTitle>系统对话</SheetTitle>
          <SheetDescription>
            用草稿 phone JSON 模拟客户端悬浮聊天：先走引导页，再走任务问答。无需发布。
          </SheetDescription>
          {dialogue && state ? (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Badge variant="secondary">{phaseLabel(state)}</Badge>
              <Badge variant="outline">{dialogue.guide.name}</Badge>
              <span className="text-[0.625rem] text-muted-foreground">
                对 {dialogue.player.name}
              </span>
            </div>
          ) : null}
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col px-6 pb-2">
          {!versionId ? (
            <Empty className="border border-border py-8">
              <EmptyHeader>
                <EmptyTitle>没有可预览的版本</EmptyTitle>
              </EmptyHeader>
            </Empty>
          ) : query.isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spinner />
            </div>
          ) : query.isError ? (
            <Alert variant="destructive">
              <AlertTitle>无法读取 phone JSON</AlertTitle>
              <AlertDescription>
                系统对话需要当前预览版本的草稿包。
              </AlertDescription>
            </Alert>
          ) : !hasPlayableDialogue(dialogue) || !state ? (
            <Empty className="border border-border py-8">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <HugeiconsIcon icon={Message01Icon} />
                </EmptyMedia>
                <EmptyTitle>没有可播放的系统对话</EmptyTitle>
                <EmptyDescription>
                  需要 0.2 的 game.guideSteps / game.puzzleSteps。旧版卡片引导不会在这里播放。
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className="min-h-0 flex-1 rounded-lg border border-border">
              <MessageScrollerProvider autoScroll>
                <MessageScroller className="h-full min-h-80">
                  <MessageScrollerViewport>
                    <MessageScrollerContent className="gap-4 p-3">
                      {state.lines.map((line) => (
                        <MessageScrollerItem
                          key={line.id}
                          messageId={line.id}
                          scrollAnchor={line.role === "player"}
                        >
                          {line.role === "marker" ? (
                            <Marker variant="separator">
                              <MarkerContent>{line.body}</MarkerContent>
                            </Marker>
                          ) : line.role === "closing" ? (
                            <div className="flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-3">
                              <p className="text-xs/relaxed">{line.statement}</p>
                              <Markdown
                                markdown={line.truthReview}
                                className="max-w-none text-xs/relaxed"
                              />
                            </div>
                          ) : (
                            <Message
                              align={line.role === "player" ? "end" : "start"}
                            >
                              {line.role === "guide" ? (
                                <MessageAvatar>
                                  <PersonAvatar person={dialogue.guide} />
                                </MessageAvatar>
                              ) : null}
                              <MessageContent>
                                <MessageHeader>
                                  {line.role === "player"
                                    ? dialogue.player.name
                                    : dialogue.guide.name}
                                </MessageHeader>
                                <Bubble
                                  variant={
                                    line.role === "player"
                                      ? "default"
                                      : "secondary"
                                  }
                                  align={
                                    line.role === "player" ? "end" : "start"
                                  }
                                >
                                  <BubbleContent className="whitespace-pre-wrap">
                                    {line.body}
                                  </BubbleContent>
                                </Bubble>
                              </MessageContent>
                            </Message>
                          )}
                        </MessageScrollerItem>
                      ))}
                    </MessageScrollerContent>
                  </MessageScrollerViewport>
                  <MessageScrollerButton />
                </MessageScroller>
              </MessageScrollerProvider>
            </div>
          )}
        </div>

        {hasPlayableDialogue(dialogue) && state ? (
          <SheetFooter className="gap-3">
            {state.input.type === "choice" ? (
              <div className="flex flex-col gap-2">
                {state.input.choices.map((choice, index) => (
                  <Button
                    key={`${choice.label}-${index}`}
                    type="button"
                    variant="outline"
                    className="h-auto min-h-7 justify-start whitespace-normal py-1.5 text-left"
                    onClick={() =>
                      setState(pickChoice(dialogue, state, index))
                    }
                  >
                    {choice.label}
                  </Button>
                ))}
              </div>
            ) : null}

            {state.input.type === "puzzle" ? (
              <FieldGroup>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant={state.input.isFree ? "success" : "warning"}>
                    {state.input.isFree ? "免费关" : "付费关"}
                  </Badge>
                  <span className="text-[0.625rem] text-muted-foreground">
                    参考答案：{state.input.correctAnswer || "（空）"}
                  </span>
                </div>
                {state.input.hintLevel >= 1 &&
                state.input.hints.level_1 ? (
                  <p className="rounded-md bg-muted/40 px-2 py-1.5 text-xs/relaxed text-muted-foreground">
                    一级：{state.input.hints.level_1}
                  </p>
                ) : null}
                {state.input.hintLevel >= 2 && state.input.hints.level_2 ? (
                  <p className="rounded-md bg-muted/40 px-2 py-1.5 text-xs/relaxed text-muted-foreground">
                    二级：{state.input.hints.level_2}
                  </p>
                ) : null}
                {state.input.hintLevel >= 3 && state.input.correctAnswer ? (
                  <p className="rounded-md bg-muted/40 px-2 py-1.5 text-xs/relaxed text-muted-foreground">
                    三级：{state.input.correctAnswer}
                  </p>
                ) : null}
                <Field>
                  <Textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    className="max-h-28 overflow-y-auto"
                    placeholder={`${dialogue.player.name} 的自由作答`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault();
                        sendAnswer();
                      }
                    }}
                  />
                </Field>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    disabled={!draft.trim()}
                    onClick={sendAnswer}
                  >
                    <HugeiconsIcon icon={SentIcon} data-icon="inline-start" />
                    发送
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setState(judgePuzzle(dialogue, state, true, draft));
                      setDraft("");
                    }}
                  >
                    判对
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setState(judgePuzzle(dialogue, state, false, draft));
                      setDraft("");
                    }}
                  >
                    判错
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={state.input.hintLevel >= 3}
                    onClick={() => setState(revealHint(state))}
                  >
                    {state.input.hintLevel === 0
                      ? "一级提示"
                      : state.input.hintLevel === 1
                        ? "二级提示"
                        : state.input.hintLevel === 2
                          ? "三级提示"
                          : "提示已用完"}
                  </Button>
                </div>
              </FieldGroup>
            ) : null}

            {state.input.type === "done" ? (
              <p className="text-xs/relaxed text-muted-foreground">
                引导与任务已走完。这是预览模拟，不是 iOS 客户端本身。
              </p>
            ) : null}

            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => restart(dialogue, "guide")}
              >
                <HugeiconsIcon icon={RefreshIcon} data-icon="inline-start" />
                重开引导
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => restart(dialogue, "puzzles")}
              >
                跳到任务
              </Button>
            </div>
          </SheetFooter>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
